import React, { useState, useEffect, useCallback, useRef } from "react";
import { searchMentees, fetchPartners } from "utils/api";
import { useDebounce } from "utils/hooks/useDebounce";
import MenteeCard from "../MenteeCard";
import {
  Input,
  Modal,
  Result,
  Spin,
  theme,
  Typography,
  Select,
  Affix,
  Button,
  FloatButton,
  Pagination,
  Switch,
  Empty,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import "../css/Gallery.scss";
import { useLocation } from "react-router";
import { getRole } from "utils/auth.service";
import { ACCOUNT_TYPE } from "utils/consts";
import { useAuth } from "../../utils/hooks/useAuth";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getTranslatedOptions } from "utils/translations";
import { css } from "@emotion/css";

const { Title, Text } = Typography;

const PAGE_SIZE = 24;

function Gallery(props) {
  const {
    token: { colorPrimaryBg, colorPrimary },
  } = theme.useToken();
  const { t } = useTranslation();
  const options = useSelector((state) => state.options);
  const { isAdmin } = useAuth();
  const [mentees, setMentees] = useState([]);
  const [total, setTotal] = useState(0);
  const [languages, setLanguages] = useState([]);
  const [interests, setInterests] = useState([]);
  const [query, setQuery] = useState();
  const [locationQuery, setLocationQuery] = useState();
  const [gender, setGender] = useState(undefined);
  const [mobileFilterVisible, setMobileFilterVisible] = useState(false);
  const location = useLocation();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allPartners, setAllPartners] = useState([]);
  const [selectedPartnerOrg, setSelectedPartnerOrg] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPrivate, setShowPrivate] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const verified = location.state && location.state.verified;
  const user = useSelector((state) => state.user.user);
  const isAdminView = isAdmin || props.isSupport;

  const debouncedQuery = useDebounce(query, 300);
  const debouncedLocationQuery = useDebounce(locationQuery, 300);

  const genderOptions = [
    { value: "woman", label: t("common.woman", "Woman") },
    { value: "man", label: t("common.man", "Man") },
    { value: "non-binary", label: t("common.nonBinary", "Non-binary") },
    {
      value: "prefer not to say",
      label: t("common.preferNotToSay", "Prefer not to say"),
    },
  ];

  // Fetch partner dropdown options (unchanged)
  useEffect(() => {
    async function getAllPartners() {
      var all_data = [];
      if (isAdmin) {
        all_data = await fetchPartners(undefined, null);
      } else {
        if (user && user.pair_partner && user.pair_partner.restricted) {
          all_data = [user.pair_partner];
        } else {
          all_data = await fetchPartners(false, null);
        }
      }
      var temp = [];
      if (Array.isArray(all_data)) {
        all_data.forEach((item) => {
          const partnerId = item?.id || item?._id?.$oid;
          if (!partnerId || !item?.organization) return;
          temp.push({
            value: item.organization + "_" + partnerId,
            label: item.organization,
          });
        });
      }
      setAllPartners(temp);
    }
    getAllPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Race condition guard
  const requestIdRef = useRef(0);

  // Server-side data fetch
  const fetchPage = useCallback(
    async (page) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);

      const params = {
        page: page,
        page_size: PAGE_SIZE,
      };

      if (debouncedQuery) {
        params.search = debouncedQuery;
      }
      if (debouncedLocationQuery) {
        params.location = debouncedLocationQuery;
      }
      if (languages.length > 0) {
        params.languages = languages.join(",");
      }
      if (interests.length > 0) {
        params.specializations = interests.join(",");
      }
      if (gender) {
        params.gender = gender;
      }
      if (Array.isArray(selectedPartnerOrg) && selectedPartnerOrg.length > 0) {
        const partnerIds = selectedPartnerOrg.map((org) => {
          const parts = String(org).split("_");
          return parts[parts.length - 1];
        });
        params.partner_ids = partnerIds.join(",");
      }
      if (isAdminView) {
        params.all = "true";
      }
      if (showPrivate) {
        params.show_private = "true";
      }
      if (user && user.pair_partner && user.pair_partner.restricted) {
        const partnerId =
          user.pair_partner.id || user.pair_partner._id?.["$oid"];
        if (partnerId) {
          params.restricted_partner_id = partnerId;
        }
      } else if (!isAdmin) {
        params.exclude_restricted = "true";
      }

      try {
        const result = await searchMentees(params);
        if (requestId !== requestIdRef.current) return; // stale response
        if (result) {
          setMentees(result.accounts || []);
          setTotal(result.total || 0);
        } else {
          setMentees([]);
          setTotal(0);
        }
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to fetch mentees:", err);
        setMentees([]);
        setTotal(0);
      } finally {
        if (requestId === requestIdRef.current) {
          setPageLoaded(true);
          setLoading(false);
        }
      }
    },
    [
      debouncedQuery,
      debouncedLocationQuery,
      languages,
      interests,
      gender,
      selectedPartnerOrg,
      isAdminView,
      showPrivate,
      isAdmin,
      user,
    ]
  );

  // When filters change, reset to page 1 and fetch
  useEffect(() => {
    setCurrentPage(1);
    fetchPage(1);
  }, [fetchPage]);

  // When only page changes (user clicks pagination), fetch that page
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClearFilters = () => {
    setQuery(undefined);
    setLanguages([]);
    setInterests([]);
    setSelectedPartnerOrg(undefined);
    setLocationQuery(undefined);
    setGender(undefined);
    setShowPrivate(false);
  };

  const getFilterForm = () => (
    <>
      <Title
        level={4}
        className={css`
          margin-top: 0;
        `}
      >
        {t("gallery.filterBy")}
      </Title>
      <Input
        placeholder={t(
          "gallery.searchByNameOrEmail",
          "Search by name or email"
        )}
        prefix={<SearchOutlined />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        allowClear
      />
      <Title level={4}>{t("commonProfile.location", "Location")}</Title>
      <Input
        placeholder={t("gallery.searchByLocation", "Search by location")}
        prefix={<SearchOutlined />}
        value={locationQuery}
        onChange={(e) => setLocationQuery(e.target.value)}
        allowClear
      />
      <Title
        level={4}
        className={css`
          color: ${colorPrimary};
        `}
      >
        {t("common.partner")}
      </Title>
      <Select
        onChange={(value) => {
          setSelectedPartnerOrg(value);
        }}
        value={selectedPartnerOrg}
        placeholder={t("common.partner")}
        options={allPartners}
        suffixIcon={<SearchOutlined />}
        className={css`
          width: 100%;
        `}
        allowClear
        mode="multiple"
        maxTagCount="responsive"
      />
      <Title level={4}>{t("common.languages")}</Title>
      <Select
        className={css`
          width: 100%;
        `}
        allowClear
        value={languages}
        mode="multiple"
        placeholder={t("common.languages")}
        options={options.languages}
        onChange={(selected) => setLanguages(selected)}
        maxTagCount="responsive"
      />
      <Title level={4}>{t("gallery.menteeInterests")}</Title>
      <Select
        className={css`
          width: 100%;
        `}
        allowClear
        value={interests}
        mode="multiple"
        placeholder={t("gallery.menteeInterests")}
        options={options.specializations}
        onChange={(selected) => setInterests(selected)}
        maxTagCount="responsive"
      />
      <Title level={4}>{t("common.gender", "Gender")}</Title>
      <Select
        className={css`
          width: 100%;
        `}
        allowClear
        value={gender}
        placeholder={t("common.gender", "Gender")}
        options={genderOptions}
        onChange={(selected) => setGender(selected)}
      />
      {isAdminView && (
        <div
          className={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: 12px;
            padding: 8px;
            background: ${colorPrimaryBg};
            border-radius: 6px;
          `}
        >
          <Text>
            {t("gallery.showPrivateProfiles", "Show private profiles")}
          </Text>
          <Switch
            checked={showPrivate}
            onChange={(checked) => setShowPrivate(checked)}
            size="small"
          />
        </div>
      )}
      <Button
        onClick={handleClearFilters}
        className={css`
          margin-top: 12px;
          width: 100%;
        `}
      >
        {t("gallery.clearFilters", "Clear Filters")}
      </Button>
    </>
  );

  // Add some kind of error 403 code
  const role = getRole();
  return role === ACCOUNT_TYPE.PARTNER ? (
    <Result
      status="403"
      title="403"
      subTitle={t("gallery.unauthorizedAccess")}
    />
  ) : (
    <>
      <Affix offsetTop={10}>
        <Button
          onClick={() => setMobileFilterVisible(true)}
          className={css`
            display: none;
            @media only screen and (max-width: 640px) {
              margin-top: 2%;
              margin-left: 2%;
              display: grid;
            }
          `}
          type="primary"
        >
          {t("gallery.filter")}
        </Button>
      </Affix>
      <Modal
        onCancel={() => {
          setMobileFilterVisible(false);
        }}
        open={mobileFilterVisible}
        footer={[
          <Button onClick={() => setMobileFilterVisible(false)} type="primary">
            {t("common.apply")}
          </Button>,
          <Button
            onClick={() => {
              setMobileFilterVisible(false);
              handleClearFilters();
            }}
          >
            {t("common.cancel")}
          </Button>,
        ]}
        styles={{
          body: {
            padding: "1rem",
          },
        }}
      >
        {getFilterForm()}
      </Modal>
      <div className="gallery-container">
        <FloatButton.BackTop />
        <Affix offsetTop={10}>
          <div
            className={css`
              margin-right: 1em;
              padding: 1em;
              border-radius: 8px;
              height: fit-content;
              border: 2px solid ${colorPrimaryBg};

              @media only screen and (max-width: 640px) {
                display: none;
              }
            `}
          >
            {getFilterForm()}
          </div>
        </Affix>

        {!pageLoaded ? (
          <div
            className={css`
              display: flex;
              flex: 1;
              justify-content: center;
              align-items: center;
              height: 80vh;
            `}
          >
            <Spin size="large" spinning />
          </div>
        ) : (
          <div
            className={css`
              flex: 5;
              display: flex;
              flex-direction: column;
            `}
          >
            <div
              className={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;
                padding: 0 4px;
              `}
            >
              <Text type="secondary">
                {t("gallery.showingResults", {
                  defaultValue: "Showing {{from}}-{{to}} of {{total}} results",
                  from: total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
                  to: Math.min(currentPage * PAGE_SIZE, total),
                  total: total,
                })}
              </Text>
              {loading && <Spin size="small" />}
            </div>
            {mentees.length === 0 && !loading ? (
              <Empty
                description={t(
                  "gallery.noResults",
                  "No results found. Try adjusting your filters."
                )}
                className={css`
                  margin-top: 80px;
                `}
              />
            ) : (
              <div className="gallery-mentor-container">
                {mentees.map((mentee, key) => {
                  const menteeId = mentee?._id?.$oid || mentee?.id;
                  if (!menteeId) return null;
                  return (
                    <MenteeCard
                      key={menteeId}
                      name={mentee.name}
                      languages={getTranslatedOptions(
                        mentee.languages,
                        options.languages
                      )}
                      location={mentee.location}
                      gender={mentee.gender}
                      organization={mentee.organization}
                      image={mentee.image}
                      video={mentee.video}
                      age={mentee.age}
                      id={menteeId}
                      pair_partner={mentee.pair_partner}
                      isSupport={props.isSupport}
                      isPrivate={mentee.is_private}
                      showAdminBadges={isAdmin || props.isSupport}
                    />
                  );
                })}
              </div>
            )}
            {total > PAGE_SIZE && (
              <div
                className={css`
                  display: flex;
                  justify-content: center;
                  margin: 24px 0 75px;
                `}
              >
                <Pagination
                  current={currentPage}
                  total={total}
                  pageSize={PAGE_SIZE}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showQuickJumper={total > PAGE_SIZE * 5}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default Gallery;
